/**
 * Inventory Offline Service
 *
 * Extends BaseOfflineService for inventory item operations.
 */

import type { Table } from 'dexie';

import { offlineDb } from '../database/offlineDatabase';
import { BaseOfflineService } from './BaseOfflineService';
import type {
  EntityType,
  OfflineEntity,
  ApiServiceMethods,
} from '../types/offline.types';

// Import inventory types
import type {
  InventoryItem,
  CreateInventoryInput,
  UpdateInventoryInput,
} from '#/entity';
import { InventoryUnit } from '@/types/enum';

// Import online service
import * as inventoryService from '@/api/services/inventoryService';

// ============================================
// OFFLINE SERVICE
// ============================================

export class InventoryOfflineService extends BaseOfflineService<
  InventoryItem,
  CreateInventoryInput,
  UpdateInventoryInput
> {
  protected readonly entityType: EntityType = 'inventory';
  protected readonly table: Table<OfflineEntity<InventoryItem>, string> = offlineDb.inventory;

  protected readonly apiService: ApiServiceMethods<
    InventoryItem,
    CreateInventoryInput,
    UpdateInventoryInput
  > = {
    getAll: (filters) => inventoryService.getAll(filters as Record<string, string | undefined>),
    getOne: inventoryService.getOne,
    create: inventoryService.create,
    update: inventoryService.update,
    delete: inventoryService.deleteInventory,
  };

  // ============================================
  // INVENTORY-SPECIFIC METHODS
  // ============================================

  /**
   * Get active inventory items
   */
  async getActive(): Promise<InventoryItem[]> {
    const items = await this.table.where('isActive').equals(1).toArray();
    return items.map(this.stripOfflineMetadata);
  }

  /**
   * Get items by unit
   */
  async getByUnit(unit: InventoryUnit): Promise<InventoryItem[]> {
    const items = await this.table.filter((i) => i.unit === unit).toArray();
    return items.map(this.stripOfflineMetadata);
  }

  /**
   * Get low stock items (quantity < minQuantity)
   */
  async getLowStock(): Promise<InventoryItem[]> {
    const items = await this.table
      .filter((i) => i.isActive && i.quantity < i.minQuantity)
      .toArray();
    return items.map(this.stripOfflineMetadata);
  }

  /**
   * Search inventory by name or SKU
   */
  async search(searchTerm: string): Promise<InventoryItem[]> {
    const term = searchTerm.toLowerCase();
    const items = await this.table
      .filter((i) => {
        const name = i.name.toLowerCase();
        const sku = i.sku?.toLowerCase() ?? '';
        return name.includes(term) || sku.includes(term);
      })
      .toArray();
    return items.map(this.stripOfflineMetadata);
  }

  /**
   * Get items for sale (not internal consumption)
   */
  async getForSale(): Promise<InventoryItem[]> {
    const items = await this.table
      .filter((i) => i.isActive && !i.isInternalConsumption)
      .toArray();
    return items.map(this.stripOfflineMetadata);
  }

  /**
   * Override applyFilters for inventory-specific filtering
   */
  protected override applyFilters(
    items: OfflineEntity<InventoryItem>[],
    filters: Record<string, unknown>
  ): OfflineEntity<InventoryItem>[] {
    return items.filter((item) => {
      // Skip deleted items
      if (item.deletedAt) {
        return false;
      }

      // Unit filter
      if (filters.unit && item.unit !== filters.unit) {
        return false;
      }

      // Branch filter
      if (filters.branchId && item.branchId !== filters.branchId) {
        return false;
      }

      // Internal consumption filter
      if (filters.excludeInternalConsumption && item.isInternalConsumption) {
        return false;
      }

      // Search filter
      if (filters.search) {
        const searchTerm = (filters.search as string).toLowerCase();
        const name = item.name.toLowerCase();
        const sku = item.sku?.toLowerCase() ?? '';
        const description = item.description?.toLowerCase() ?? '';
        if (
          !name.includes(searchTerm) &&
          !sku.includes(searchTerm) &&
          !description.includes(searchTerm)
        ) {
          return false;
        }
      }

      return true;
    });
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const inventoryOfflineService = new InventoryOfflineService();
